import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { Box, Paper } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useEnqueueSnackbar } from 'hooks';
import type { PackEntry } from 'types/shared';
import { useTestCreatives, useUpdateHypothesisSection } from '../../api';
import { useFilterOptions } from '../../api/getFilterOptions';
import useHypothesisGrouping from '../../hooks/useHypothesisGrouping';
import useCreativeFilters from '../../hooks/useCreativeFilters';
import useCreativeColumnVisibility from '../../hooks/useCreativeColumnVisibility';
import useTestCreativesViewState from '../../hooks/useTestCreativesViewState';
import useCreativesByPack, { type HypothesisPack, NO_HYPOTHESIS_KEY, } from '../../hooks/useCreativesByPack';
import type { HypothesisGroup, HypothesisSection, UploadedFilter, } from '../../types/hypothesis';
import CreativesFilterBar from './CreativesFilterBar';
import TestCreativesContent from './TestCreativesContent';
import TestCreativesHeader from './TestCreativesHeader';
import LoadingOverlay from './LoadingOverlay';
import { ALL_COLUMN_DEFS } from './creativeColumns';
import useFiltersActive from '../../hooks/useFiltersActive';

const EMPTY_PACKS: PackEntry[] = [];
const EMPTY_NO_HYPOTHESIS_PACKS: HypothesisPack[] = [];

const useOptionalFormContext = (): UseFormReturn | null => {
  try {
    return useFormContext();
  } catch {
    return null;
  }
};

interface TestCreativesListProps {
  defaultApp?: string;
}

const TestCreativesList: FC<TestCreativesListProps> = ({ defaultApp }) => {
  const { app, network, selectedCreatives, updateSelection } =
    useTestCreativesViewState({ defaultApp });

  const formContext = useOptionalFormContext();
  const packsWithCreatives =
    (formContext?.watch('packsWithCreatives') as PackEntry[] | undefined) ??
    EMPTY_PACKS;

  const [groupByHypothesis, setGroupByHypothesis] = useState(true);
  const [uploadedFilter, setUploadedFilter] = useState<UploadedFilter>('last_week');

  const {
    filters,
    setHypothesis,
    setSearch,
    setFlow,
    setCustomer,
    setTechnology,
    setTpcFinishDate,
    setStatus,
  } = useCreativeFilters([]);

  const { data: displayCreatives = [], isLoading, isFetching } = useTestCreatives({
    params: {
      app,
      hypothesis: filters.hypothesis,
      customer: filters.customer ?? undefined,
      flow: filters.flow,
      technology: filters.technology,
      status: filters.status ?? undefined,
      query: filters.search || undefined,
      tpc_finish_date: filters.tpcFinishDate ?? undefined,
      uploaded: uploadedFilter,
    },
    config: { enabled: !!app },
  });

  const hasPacks = packsWithCreatives.length > 0;
  const { data: unfilteredCreatives = [] } = useTestCreatives({
    params: { app },
    config: { enabled: !!app && hasPacks },
  });

  const { data: filterOptionsData } = useFilterOptions({
    params: { app, network },
    config: { enabled: !!app },
  });

  const filterOptions = useMemo(
    () => ({
      hypotheses: filterOptionsData?.hypothesis ?? [],
      customers: filterOptionsData?.customer ?? [],
      flows: filterOptionsData?.flow ?? [],
      technologies: filterOptionsData?.technology ?? [],
    }),
    [filterOptionsData],
  );

  const {
    visibilityModel,
    columnOrder,
    toggleColumn,
    moveColumn,
    resetDefaults,
  } = useCreativeColumnVisibility();

  const visibleColumns = useMemo<GridColDef[]>(() => {
    return columnOrder
      .filter((key) => visibilityModel[key])
      .map((key) => ALL_COLUMN_DEFS[key])
      .filter(Boolean);
  }, [columnOrder, visibilityModel]);

  const { onShowErrorAlert } = useEnqueueSnackbar();

  const updateHypothesisSection = useUpdateHypothesisSection({
    config: {
      onError: (err) => {
        const message =
          (err.cause as { details?: string } | undefined)?.details ??
          'Failed to update hypothesis section';
        onShowErrorAlert(message, { delayAlert: 3500 });
      },
    },
  });

  const { packsByHypothesis, unpackedCreatives } = useCreativesByPack(
    packsWithCreatives,
    unfilteredCreatives,
    displayCreatives,
  );

  const noHypothesisPacks = useMemo(
    () => packsByHypothesis.get(NO_HYPOTHESIS_KEY) ?? EMPTY_NO_HYPOTHESIS_PACKS,
    [packsByHypothesis],
  );

  const {
    thisWeek: unpackedThisWeek,
    upcoming: unpackedUpcoming,
    noHypothesis,
    hypothesisCount: unpackedHypothesisCount,
  } = useHypothesisGrouping(unpackedCreatives);

  const { thisWeek, upcoming, hypothesisCount } = useMemo(() => {
    const knownHypotheses = new Set<string>();
    for (const g of unpackedThisWeek) knownHypotheses.add(g.hypothesis);
    for (const g of unpackedUpcoming) knownHypotheses.add(g.hypothesis);

    const extraThisWeek: HypothesisGroup[] = [];
    const extraUpcoming: HypothesisGroup[] = [];

    for (const [hypothesisKey, packs] of packsByHypothesis) {
      if (hypothesisKey === NO_HYPOTHESIS_KEY) continue;
      if (knownHypotheses.has(hypothesisKey)) continue;

      const firstCreativeSection =
        packs.find((p) => p.creatives.length > 0)?.creatives[0]?.section ?? null;

      const syntheticGroup: HypothesisGroup = {
        hypothesis: hypothesisKey,
        section: firstCreativeSection,
        creatives: [],
      };
      if (firstCreativeSection === 'this_week') {
        extraThisWeek.push(syntheticGroup);
      } else {
        extraUpcoming.push(syntheticGroup);
      }
    }

    const sortByHypothesisName = (a: HypothesisGroup, b: HypothesisGroup) =>
      a.hypothesis.localeCompare(b.hypothesis);

    const mergedThisWeek =
      extraThisWeek.length === 0
        ? unpackedThisWeek
        : [...unpackedThisWeek, ...extraThisWeek].sort(sortByHypothesisName);
    const mergedUpcoming =
      extraUpcoming.length === 0
        ? unpackedUpcoming
        : [...unpackedUpcoming, ...extraUpcoming].sort(sortByHypothesisName);

    return {
      thisWeek: mergedThisWeek,
      upcoming: mergedUpcoming,
      hypothesisCount:
        unpackedHypothesisCount + extraThisWeek.length + extraUpcoming.length,
    };
  }, [unpackedThisWeek, unpackedUpcoming, unpackedHypothesisCount, packsByHypothesis]);

  const allCreativeIds = useMemo(
    () => unpackedCreatives.map((c) => c.id),
    [unpackedCreatives],
  );

  const allSelected =
    allCreativeIds.length > 0 &&
    allCreativeIds.every((id) => selectedCreatives.includes(id));
  const someSelected =
    !allSelected && allCreativeIds.some((id) => selectedCreatives.includes(id));

  const handleToggleSelectAll = useCallback(() => {
    updateSelection(allSelected ? [] : allCreativeIds, { syncActivePack: false });
  }, [allSelected, allCreativeIds, updateSelection]);

  const handleSelectionChange = useCallback(
    (ids: string[]) => { updateSelection(ids, { syncActivePack: false }); },
    [updateSelection],
  );

  const selectedCreativesRef = useRef(selectedCreatives);
  useEffect(() => {
    selectedCreativesRef.current = selectedCreatives;
  }, [selectedCreatives]);

  const idsByHypothesisRef = useRef(new Map<string, string[]>());
  useEffect(() => {
    const map = new Map<string, string[]>();
    const addId = (hypothesisKey: string, creativeId: string) => {
      const existing = map.get(hypothesisKey);
      if (existing) existing.push(creativeId);
      else map.set(hypothesisKey, [creativeId]);
    };
    const ingestGroups = (groups: HypothesisGroup[]) => {
      for (const group of groups) {
        for (const c of group.creatives) {
          addId(group.hypothesis, c.id);
        }
      }
    };
    ingestGroups(thisWeek);
    ingestGroups(upcoming);
    for (const c of noHypothesis) {
      addId(NO_HYPOTHESIS_KEY, c.id);
    }
    for (const pack of noHypothesisPacks) {
      for (const c of pack.creatives) {
        addId(NO_HYPOTHESIS_KEY, c.id);
      }
    }
    idsByHypothesisRef.current = map;
  }, [thisWeek, upcoming, noHypothesis, noHypothesisPacks]);

  const handleGroupSelectionChange = useCallback(
    (hypothesis: string, ids: string[]) => {
      const groupIds = idsByHypothesisRef.current.get(hypothesis) ?? [];
      const groupSet = new Set(groupIds);
      const others = selectedCreativesRef.current.filter((id) => !groupSet.has(id));
      updateSelection([...others, ...ids], { syncActivePack: false });
    },
    [updateSelection],
  );

  const displayCreativesRef = useRef(displayCreatives);
  useEffect(() => {
    displayCreativesRef.current = displayCreatives;
  }, [displayCreatives]);

  const formContextRef = useRef(formContext);
  useEffect(() => {
    formContextRef.current = formContext;
  }, [formContext]);

  const handlePackSelectionChange = useCallback(
    (packIndex: number, ids: string[]) => {
      const ctx = formContextRef.current;
      if (!ctx) return;
      const currentPacks =
        (ctx.getValues('packsWithCreatives') as PackEntry[] | undefined) ?? [];
      const targetPack = currentPacks[packIndex];
      if (!targetPack) return;

      if (ids.length === 0) {
        const nextPacks = currentPacks.filter((_, index) => index !== packIndex);
        ctx.setValue('packsWithCreatives', nextPacks, { shouldValidate: true });
        const activeIndex = ctx.getValues('activePacksWithCreatives') as
          | number
          | null;
        if (activeIndex === packIndex) {
          ctx.setValue('activePacksWithCreatives', null);
        } else if (activeIndex !== null && activeIndex > packIndex) {
          ctx.setValue('activePacksWithCreatives', activeIndex - 1);
        }
        return;
      }

      const nextPacks = currentPacks.map((pack, index) =>
        index === packIndex ? { ...pack, creatives: ids } : pack,
      );
      ctx.setValue('packsWithCreatives', nextPacks, { shouldValidate: true });
    },
    [],
  );

  const handleSectionChange = useCallback(
    (hypothesis: string, section: HypothesisSection) => {
      const list = displayCreativesRef.current;
      const creatives =
        hypothesis === NO_HYPOTHESIS_KEY
          ? list
              .filter((c) => !c.hypothesis)
              .map((c) => ({
                creative_id: c.creative_id,
                creative_type: c.creative_type,
              }))
          : list
              .filter((c) => c.hypothesis === hypothesis)
              .map((c) => ({
                creative_id: c.creative_id,
                creative_type: c.creative_type,
              }));
      if (creatives.length === 0) return;
      updateHypothesisSection.mutate({ creatives, test_period: section });
    },
    [updateHypothesisSection],
  );

  const sortedFlatCreatives = useMemo(
    () =>
      [...displayCreatives].sort((a, b) => {
        if (a.launch_status === 'launched' && b.launch_status !== 'launched') return -1;
        if (a.launch_status !== 'launched' && b.launch_status === 'launched') return 1;
        return new Date(b.uploaded).getTime() - new Date(a.uploaded).getTime();
      }),
    [displayCreatives],
  );

  const hasActiveFilters = useFiltersActive(filters, uploadedFilter);

  const handleClearFilters = useCallback(() => {
    setSearch('');
    setHypothesis([]);
    setFlow([]);
    setCustomer(null);
    setTechnology([]);
    setTpcFinishDate(null);
    setStatus(null);
    setUploadedFilter('last_week');
  }, [
    setSearch,
    setHypothesis,
    setFlow,
    setCustomer,
    setTechnology,
    setTpcFinishDate,
    setStatus,
  ]);

  const isInitialLoading = isLoading;
  const isRefetching = !isLoading && isFetching;
  const packedTotal = useMemo(() => {
    let total = 0;
    for (const packs of packsByHypothesis.values()) {
      for (const pack of packs) {
        total += pack.creatives.length;
      }
    }
    return total;
  }, [packsByHypothesis]);
  const totalCreatives = unpackedCreatives.length + packedTotal;
  const hasResults = totalCreatives > 0;

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, width: '100%' }}>
      <TestCreativesHeader
        totalCreatives={totalCreatives}
        hypothesisCount={hypothesisCount}
        groupByHypothesis={groupByHypothesis}
        onGroupByHypothesisChange={setGroupByHypothesis}
        allSelected={allSelected}
        someSelected={someSelected}
        onToggleSelectAll={handleToggleSelectAll}
        columnOrder={columnOrder}
        visibilityModel={visibilityModel}
        onToggleColumn={toggleColumn}
        onMoveColumn={moveColumn}
        onResetColumns={resetDefaults}
      />

      {/* Filter bar — stays mounted and interactive while the grid below refetches */}
      <Box sx={{ mb: 2.5 }}>
        <CreativesFilterBar
          search={filters.search}
          uploaded={uploadedFilter}
          hypothesis={filters.hypothesis}
          flow={filters.flow}
          customer={filters.customer}
          technology={filters.technology}
          tpcFinishDate={filters.tpcFinishDate}
          status={filters.status}
          filterOptions={filterOptions}
          onSearchChange={setSearch}
          onUploadedChange={setUploadedFilter}
          onHypothesisChange={setHypothesis}
          onFlowChange={setFlow}
          onCustomerChange={setCustomer}
          onTechnologyChange={setTechnology}
          onTpcFinishDateChange={setTpcFinishDate}
          onStatusChange={setStatus}
        />
      </Box>

      {/* Grid / groups — fallback shows ONLY here while filters refetch */}
      <LoadingOverlay
        isLoading={isRefetching}
        title="Loading test creatives..."
      >
        <TestCreativesContent
          isInitialLoading={isInitialLoading}
          hasResults={hasResults}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={handleClearFilters}
          groupByHypothesis={groupByHypothesis}
          packsByHypothesis={packsByHypothesis}
          thisWeek={thisWeek}
          upcoming={upcoming}
          noHypothesis={noHypothesis}
          sortedFlatCreatives={sortedFlatCreatives}
          selectedCreatives={selectedCreatives}
          columns={visibleColumns}
          onGroupSelectionChange={handleGroupSelectionChange}
          onPackSelectionChange={handlePackSelectionChange}
          onFlatSelectionChange={handleSelectionChange}
          onSectionChange={handleSectionChange}
        />
      </LoadingOverlay>
    </Paper>
  );
};

export default TestCreativesList;
